import { Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Constants } from '../../../admin-core/constants';
import { PRODUCT_CARD_RETRY_LATER_MS, ProductCard, ProductCardsService } from '../../product-cards.service';

/**
 * The strip of product cards under a settled Pebbl reply, for the SKUs no entry took —
 * a comparison laid out as a table, say. Each product the reply names as an entry gets its
 * picture beside that entry instead (ProductEntryComponent). The strip only renders once
 * the reply has finished streaming, so cards never flash at the bottom and then jump.
 *
 * The strip IS the reply's imagery — the text never points at it — so it is a plain
 * list of links, scrolling sideways so a 50-product range never widens the bubble.
 * Skeletons hold the final size while SKUs resolve, so the reply does not jump. The SKU
 * is never rendered: not as text, alt, title or aria-label. Styles live in ai.styles.css
 * (`.pc-*`), which the two hosts load unencapsulated. SKUs whose lookup FAILED are asked
 * again on PRODUCT_CARD_RETRY_LATER_MS; the cards already found stay up meanwhile.
 */
@Component({
  selector: 'app-ai-product-cards',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './product-cards.component.html',
})
export class ProductCardsComponent implements OnChanges {
  /* ################### Variable Declaration Start ################### */
  /** SKUs in display order, exactly as the frame(s) sent them. */
  @Input({ required: true }) skus: string[] = [];
  /** The first SKU the strip shows — the ones before it already sit beside their entries. */
  @Input() from = 0;

  cards = signal<ProductCard[]>([]);
  loading = signal(true);
  skeletons: number[] = [];

  /** The SKU list last resolved — a re-render with the same list must not fetch again. */
  private resolvedKey: string | null = null;
  private subscription?: Subscription;
  /** How many times the current list has been asked again after a lookup failed. */
  private retries = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private readonly destroyRef = inject(DestroyRef);
  /* ################### Variable Declaration End ################### */

  constructor(private productCards: ProductCardsService) {
    this.destroyRef.onDestroy(() => clearTimeout(this.retryTimer));
  }

  ngOnChanges(): void {
    const skus = (this.skus ?? []).slice(this.from);
    const key = JSON.stringify(skus);
    if (key === this.resolvedKey) return;
    this.resolvedKey = key;
    this.retries = 0;
    this.skeletons = skus.map((_, i) => i);
    this.load(skus);
  }

  /** Resolve the list (again, on a retry). A retry keeps the cards it has — no skeletons. */
  private load(skus: string[]): void {
    this.subscription?.unsubscribe();
    clearTimeout(this.retryTimer);

    // Already resolved (chat switch, widget reopen): render at once, no skeleton flash.
    const cached = this.productCards.cached(skus);
    if (cached) {
      this.cards.set(cached);
      this.loading.set(false);
      return;
    }
    if (this.retries === 0) this.loading.set(true);
    this.subscription = this.productCards
      .resolve(skus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cards) => {
        this.cards.set(cards);
        this.loading.set(false);
        if (!skus.every((sku) => this.productCards.isSettled(sku))) this.retryLater(skus);
      });
  }

  /** A lookup failed: ask again on the next PRODUCT_CARD_RETRY_LATER_MS step, if any is left. */
  private retryLater(skus: string[]): void {
    const delay = PRODUCT_CARD_RETRY_LATER_MS[this.retries];
    if (delay === undefined) return;
    this.retryTimer = setTimeout(() => {
      this.retries++;
      this.load(skus);
    }, delay);
  }

  /** Swap a broken hero image for the shared placeholder (once — no error loop). */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.src !== Constants.NO_IMAGE_PRODUCT) img.src = Constants.NO_IMAGE_PRODUCT;
  }
}
